import React, { createContext, useState, useEffect, ReactNode } from 'react';
import { Lang, TranslationKeys, createTranslator } from '../i18n';

interface I18nContextType {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: (key: keyof TranslationKeys, vars?: Record<string, string>) => string;
}

export const I18nContext = createContext<I18nContextType | undefined>(undefined);

export const I18nProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Try to get lang from WP cabinetVars, fallback to local storage or 'pl'
  const getInitialLang = (): Lang => {
    if (typeof window !== 'undefined' && window.cabinetVars?.lang) {
      return window.cabinetVars.lang;
    }
    if (typeof localStorage !== 'undefined') {
      const stored = localStorage.getItem('cab_lang') as Lang;
      if (stored === 'pl' || stored === 'uk' || stored === 'ru') return stored;
    }
    return 'pl';
  };

  const [lang, setLangState] = useState<Lang>(getInitialLang());
  const [t, setT] = useState(() => createTranslator(lang));

  useEffect(() => {
    setT(() => createTranslator(lang));
    // Set HTML lang attribute for accessibility
    document.documentElement.lang = lang === 'uk' ? 'ua' : lang;
  }, [lang]);

  const setLang = (newLang: Lang) => {
    setLangState(newLang);
    localStorage.setItem('cab_lang', newLang);
    
    // In WP, if we want to sync language change with Polylang for the rest of the site,
    // we would need to reload the page with a lang parameter or call an endpoint.
    // For a seamless React experience, we just change it locally.
    // If the user navigates away to a non-React WP page, they might lose this sync 
    // unless we use cookies that Polylang understands.
  };

  return (
    <I18nContext.Provider value={{ lang, setLang, t }}>
      {children}
    </I18nContext.Provider>
  );
};
